import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMatchEventsSocket } from './useMatchEventsSocket';
import { parseTsToMillis } from '../utils/matchEventsFeed';
import {
  SIGNAL_FIELD,
  type SniperSignalKind,
  type SniperSignalTimes,
} from '../utils/sniperEventOrder';
import type {
  MatchEventFrame,
  MatchOperation,
} from '../types/matchEvents.types';

/**
 * Live goal / VAR / reversal signals for a Sniper group, reduced to ONE
 * timestamp per fixture per kind.
 *
 * This is the FAST PATH for the page's most-recent-first card order: the 15s
 * `GET /sniper/groups` poll carries the durable stamps (and is the refresh
 * baseline), but waiting up to 15s to float a scoring match to the top is not
 * "real time". So the page joins the same `/match-events` rooms the cards
 * already use and REDUCES the feed — it never buffers it.
 *
 * Why this is cheap enough to run for a whole group:
 *  - `trackFrames` / `trackOperations` are OFF, so the socket hook keeps no
 *    frame or operation arrays for this subscription (the accumulation that was
 *    deliberately turned off on the cards stays off).
 *  - The socket is ref-counted per URL by `socket.io-react-hook`, so this adds
 *    ROOMS, not a second connection.
 *  - The reducer returns the SAME Map reference whenever the incoming frame is
 *    not a newer signal — which is the overwhelming majority of traffic — so
 *    the page does not re-render on ordinary feed volume. Only a genuinely
 *    newer goal / VAR / reversal produces a new Map and one re-order.
 */

/** Incident categories that count as a goal-confidence signal, at ANY %. */
const GOAL_SIGNAL_CATEGORIES: ReadonlySet<string> = new Set(['goal', 'penalty']);

/** Op types that report a goal being retracted / reversed. */
export const REVERSAL_OP_TYPES: ReadonlySet<string> = new Set([
  // A low-confidence goal reading retracting — notification only, never trades.
  'sniper_goal_retracted',
  // The VAR-defense SELL: the goal we swept on was annulled.
  'sniper_var_reversal',
]);

/**
 * Which signal an incident frame carries, or null when it carries none.
 * Confidence is NOT read: the owner wants the FIRST reading of a goal to float
 * the match, at any percentage — waiting for a confirmed score change is
 * exactly the behaviour being replaced.
 */
export function frameSignalKind(
  frame: Pick<MatchEventFrame, 'category'>,
): SniperSignalKind | null {
  if (GOAL_SIGNAL_CATEGORIES.has(frame.category)) return 'goal';
  if (frame.category === 'var') return 'var';
  return null;
}

/** Which signal an operation carries, or null. */
export function operationSignalKind(
  op: Pick<MatchOperation, 'type'>,
): SniperSignalKind | null {
  return REVERSAL_OP_TYPES.has(op.type) ? 'reversal' : null;
}

/**
 * Fold one signal into the per-fixture map. Returns the SAME reference when the
 * stamp is absent, unparseable, or not strictly newer than what is already
 * recorded for that fixture+kind — so a replayed frame is a no-op and the
 * caller's state update can be skipped entirely.
 */
export function reduceSignal(
  prev: ReadonlyMap<number, SniperSignalTimes>,
  fixtureId: number,
  kind: SniperSignalKind,
  ts: string | null | undefined,
): ReadonlyMap<number, SniperSignalTimes> {
  const ms = parseTsToMillis(ts);
  if (!Number.isFinite(ms)) return prev;
  const field = SIGNAL_FIELD[kind];
  const existing = prev.get(fixtureId);
  if (existing && parseTsToMillis(existing[field]) >= ms) return prev;
  const next = new Map(prev);
  next.set(fixtureId, { ...(existing ?? {}), [field]: ts });
  return next;
}

/** The most recent reversal seen, with the stamp the cue gates its age on. */
export interface SniperReversal {
  /** `<fixtureId>:<ts>` — changes exactly once per distinct reversal. */
  key: string;
  /** ISO 8601 of the reversal ITSELF, not of when we observed it. */
  ts: string;
}

export interface SniperLiveSignalsApi {
  /** Newest live stamp per fixture, per kind. Merged with the REST stamps. */
  signals: ReadonlyMap<number, SniperSignalTimes>;
  /**
   * The most recent REVERSAL, or null. The `ts` matters as much as the key: the
   * gateway backfills durable ops on EVERY subscribe, so this is populated from
   * HISTORY on a fresh page load just as readily as from a live event, and the
   * consumer has to be able to tell those apart.
   */
  lastReversal: SniperReversal | null;
}

/**
 * Subscribe to `fixtureIds` on the `/match-events` feed and reduce the stream
 * to signal timestamps. Returns no frames — by design.
 */
export function useSniperLiveSignals(
  fixtureIds: number[],
): SniperLiveSignalsApi {
  const [signals, setSignals] = useState<ReadonlyMap<number, SniperSignalTimes>>(
    () => new Map(),
  );
  const [lastReversal, setLastReversal] = useState<SniperReversal | null>(null);
  // The map is mirrored in a ref so the socket callbacks can fold a signal and
  // read the result SYNCHRONOUSLY (several frames can land in one tick), and so
  // the "did this actually change anything" decision stays outside the state
  // updater — a state updater must be pure.
  const signalsRef = useRef<ReadonlyMap<number, SniperSignalTimes>>(signals);
  // The socket hook's fixture filter is a no-op while its subscribed set is
  // still empty, so pin the wanted set here too — a signal for a fixture this
  // group does not show must never enter (and grow) the map.
  const fixtureKey = [...fixtureIds].sort((a, b) => a - b).join(',');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const wanted = useMemo(() => new Set(fixtureIds), [fixtureKey]);
  const wantedRef = useRef<ReadonlySet<number>>(wanted);
  wantedRef.current = wanted;

  const apply = useCallback(
    (fixtureId: number, kind: SniperSignalKind, ts: string | null | undefined) => {
      if (!wantedRef.current.has(fixtureId)) return false;
      const next = reduceSignal(signalsRef.current, fixtureId, kind, ts);
      if (next === signalsRef.current) return false;
      signalsRef.current = next;
      setSignals(next);
      return true;
    },
    [],
  );

  const onFrame = useCallback(
    (frame: MatchEventFrame) => {
      const kind = frameSignalKind(frame);
      if (!kind) return;
      apply(frame.fixtureId, kind, frame.receivedAt);
    },
    [apply],
  );

  const onOperation = useCallback(
    (op: MatchOperation) => {
      const kind = operationSignalKind(op);
      if (!kind) return;
      // Report only a genuinely NEW reversal — a replayed op folds to the same
      // reference and must not re-arm anything downstream.
      if (apply(op.fixtureId, kind, op.ts)) {
        setLastReversal({ key: `${op.fixtureId}:${op.ts}`, ts: op.ts });
      }
    },
    [apply],
  );

  useMatchEventsSocket({
    // Nothing to watch → hold no connection. Without this the groups LANDING
    // (no group selected, so no fixtures) would open a `/match-events` socket
    // that can never receive anything.
    enabled: fixtureIds.length > 0,
    fixtureIds,
    trackFrames: false,
    trackOperations: false,
    onFrame,
    onOperation,
  });

  // Drop signals for fixtures that left the group (group switched, event
  // removed / deactivated) so a long-lived tab's map cannot grow without bound.
  useEffect(() => {
    const current = signalsRef.current;
    let stale = false;
    const next = new Map<number, SniperSignalTimes>();
    current.forEach((times, fx) => {
      if (wanted.has(fx)) next.set(fx, times);
      else stale = true;
    });
    if (!stale) return;
    signalsRef.current = next;
    setSignals(next);
  }, [wanted]);

  return { signals, lastReversal };
}
